import React, { useState } from "react";
import { Card, Button, Tooltip, Tag, Badge, Avatar, Divider } from "antd";
import {
  ClockCircleOutlined,
  DeleteOutlined,
  UserOutlined,
  CalendarOutlined,
  ScheduleOutlined,
  TeamOutlined,
} from "@ant-design/icons";
import {
  FaRegBuilding,
  FaUserMd,
  FaCalendarCheck,
  FaClock,
  FaUsers,
  FaChevronRight,
} from "react-icons/fa";

const WorkingDayCard = ({
  group,
  dayMap,
  handleAvailability,
  toggleWorkingDayStatus,
  handleDeleteWorkingDay,
}) => {
  const [isExpanded, setIsExpanded] = useState(false);

  const dayColors = {
    0: "#ff7875", // Sunday - Red
    1: "#1890ff", // Monday - Blue
    2: "#52c41a", // Tuesday - Green
    3: "#fa8c16", // Wednesday - Orange
    4: "#722ed1", // Thursday - Purple
    5: "#13c2c2", // Friday - Cyan
    6: "#eb2f96", // Saturday - Magenta
  };

  const formatTime = (time) => {
    if (!time) return "";
    const [hours, minutes] = time.split(":");
    const hour12 = hours % 12 || 12;
    const ampm = hours >= 12 ? "PM" : "AM";
    return `${hour12}:${minutes} ${ampm}`;
  };

  const getTotalSlots = (doctor) =>
    doctor.days.reduce(
      (total, day) => total + (day.availabilityHours?.length || 0),
      0,
    );

  const getTotalWorkingDays = (doctor) =>
    doctor.days.filter((day) => day.isWorking).length;

  // Filter out specialists before calculating clinic totals
  const nonSpecialistDoctors = group.doctors.filter(
    (doctor) => !doctor.isSpecialist,
  );

  const getTotalClinicSlots = () =>
    nonSpecialistDoctors.reduce(
      (total, doctor) => total + getTotalSlots(doctor),
      0,
    );

  const getTotalClinicWorkingDays = () =>
    nonSpecialistDoctors.reduce(
      (total, doctor) => total + getTotalWorkingDays(doctor),
      0,
    );

  return (
    <Card
      className="shadow-lg hover:shadow-xl transition-all duration-300 border-0 rounded-2xl overflow-hidden [&_.ant-card-body]:!p-3 sm:[&_.ant-card-body]:!p-6"
      style={{ background: "linear-gradient(135deg, #1F6F7D 0%, #298E9F 100%)" }}
    >
      {/* Clinic Header - Always Visible */}
      <div
        className="bg-white bg-opacity-10 backdrop-blur-sm p-2 md:p-3 -m-3 mb-3 sm:-m-6 sm:mb-6 cursor-pointer hover:bg-opacity-20 transition-all duration-300"
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-3 sm:gap-4 min-w-0">
            <div className="bg-white bg-opacity-20 p-2 sm:p-3 rounded-xl flex-shrink-0">
              <FaRegBuilding className="text-xl sm:text-2xl text-white" />
            </div>
            <div className="min-w-0">
              <h2 className="text-base md:text-2xl font-bold text-white mb-0 sm:mb-1 truncate">
                {group.clinicName}
              </h2>
              <div className="flex flex-wrap items-center gap-x-3 md:gap-x-4 text-white text-opacity-80 text-xs sm:text-sm">
                <div className="items-center hidden md:flex">
                  <TeamOutlined className="mr-2" />
                  <span>
                    {nonSpecialistDoctors.length} Doctor
                    {nonSpecialistDoctors.length !== 1 ? "s" : ""}
                  </span>
                </div>
                <div className="flex items-center">
                  <CalendarOutlined className="mr-1 sm:mr-2" />
                  <span>{getTotalClinicWorkingDays()} Working Days</span>
                </div>
                <div className="items-center hidden md:flex">
                  <ScheduleOutlined className="mr-2" />
                  <span>{getTotalClinicSlots()} Time Slots</span>
                </div>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-3 sm:gap-4 flex-shrink-0">
            <div className="text-right hidden sm:block">
              <Badge
                count={getTotalClinicWorkingDays()}
                style={{ backgroundColor: "#52c41a" }}
              />
              <div className="text-white text-opacity-80 text-sm mt-1">
                Active Days
              </div>
            </div>
            <div
              className="text-white text-xl sm:text-2xl transition-transform duration-300"
              style={{
                transform: isExpanded ? "rotate(90deg)" : "rotate(0deg)",
              }}
            >
              <FaChevronRight />
            </div>
          </div>
        </div>
      </div>

      {/* Collapsible Content */}
      <div
        className={`transition-all duration-300 ease-in-out overflow-hidden ${
          isExpanded ? "max-h-none opacity-100" : "max-h-0 opacity-0"
        }`}
      >
        {/* Doctors Section - Only Non-Specialists */}
        <div className="space-y-4 sm:space-y-6">
          {nonSpecialistDoctors.map((doctor, index) => (
            <div
              key={doctor.doctorId ?? "general"}
              className="bg-white backdrop-blur-sm rounded-2xl p-3 sm:p-6"
            >
              {/* Doctor Header */}
              <div className="flex items-center justify-between mb-4 sm:mb-6">
                <div className="flex items-center gap-3 sm:gap-4 min-w-0">
                  <Avatar
                    size={{ xs: 44, sm: 64 }}
                    icon={<UserOutlined />}
                    className="bg-primary border-2 sm:border-4 border-primary shadow-lg flex-shrink-0"
                  />
                  <div className="min-w-0">
                    <h3 className="text-base sm:text-xl font-semibold text-primary mb-1 truncate">
                      {doctor.doctorName}
                    </h3>
                    {doctor.specialization && (
                      <Tag color="cyan" className="rounded-full">
                        {doctor.specialization}
                      </Tag>
                    )}
                    <div className="flex items-center gap-3 sm:gap-4 mt-1 sm:mt-2 text-primary text-opacity-80 text-xs sm:text-sm">
                      <span className="flex items-center">
                        <CalendarOutlined className="mr-1" />
                        {getTotalWorkingDays(doctor)} days
                      </span>
                      <span className="flex items-center">
                        <ScheduleOutlined className="mr-1" />
                        {getTotalSlots(doctor)} slots
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Working Days Grid */}
              {doctor.days.length > 0 ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
                  {doctor.days.map((day) => (
                    <div
                      key={day.workingDayId || day.id}
                      className={`bg-primary/20 border rounded-xl p-3 sm:p-4 transition-all duration-300 hover:shadow-lg ${
                        !day.isWorking ? "opacity-60" : "sm:hover:scale-105"
                      }`}
                    >
                      {/* Day Header */}
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center space-x-2">
                          <div
                            className="w-4 h-4 rounded-full"
                            style={{ backgroundColor: dayColors[day.dayOfWeek] }}
                          />
                          <span
                            className={`font-semibold text-gray-800 ${
                              !day.isWorking && "line-through text-gray-400"
                            }`}
                          >
                            {dayMap[day.dayOfWeek]}
                          </span>
                        </div>
                        <Badge
                          count={(day.availabilityHours || []).length}
                          style={{ backgroundColor: dayColors[day.dayOfWeek] }}
                        />
                      </div>

                      {/* Time Slots */}
                      {(day.availabilityHours || []).length > 0 ? (
                        <div className="space-y-2 mb-4">
                          {day.availabilityHours.slice(0, 2).map((slot) => (
                            <div
                              key={slot.availabilityHourId}
                              className="bg-gray-50 rounded-lg p-2 text-xs"
                            >
                              <div className="flex items-center justify-between gap-2">
                                <span className="flex items-center text-gray-600">
                                  <FaClock className="mr-1 flex-shrink-0" />
                                  {formatTime(slot.startTime)} -{" "}
                                  {formatTime(slot.endTime)}
                                </span>
                                {slot.maxPatients && (
                                  <span className="flex items-center text-blue-600 flex-shrink-0">
                                    <FaUsers className="mr-1" />
                                    {slot.maxPatients}
                                  </span>
                                )}
                              </div>
                            </div>
                          ))}
                          {day.availabilityHours.length > 2 && (
                            <div className="text-center text-xs text-gray-500">
                              +{day.availabilityHours.length - 2} more slots
                            </div>
                          )}
                        </div>
                      ) : (
                        <div className="text-center text-gray-400 text-sm py-2 mb-4">
                          No time slots configured
                        </div>
                      )}

                      {/* Action Buttons */}
                      <div className="flex justify-center space-x-2">
                        <Tooltip title="Manage Availability">
                          <Button
                            type="primary"
                            icon={<ClockCircleOutlined />}
                            size="small"
                            className="rounded-full"
                            style={{
                              backgroundColor: dayColors[day.dayOfWeek],
                              borderColor: dayColors[day.dayOfWeek],
                            }}
                            onClick={() => handleAvailability(day)}
                          />
                        </Tooltip>
                        <Tooltip title="Delete Working Day">
                          <Button
                            danger
                            icon={<DeleteOutlined />}
                            size="small"
                            className="rounded-full"
                            onClick={() =>
                              handleDeleteWorkingDay(day.workingDayId || day.id)
                            }
                          />
                        </Tooltip>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center text-gray-400 py-8">
                  <FaCalendarCheck className="text-4xl mb-4 mx-auto opacity-50" />
                  <p className="text-lg">No working days configured</p>
                  <p className="text-sm">Add working days to get started</p>
                </div>
              )}

              {/* Divider between doctors */}
              {index < nonSpecialistDoctors.length - 1 && (
                <Divider className="border-white border-opacity-20 my-6" />
              )}
            </div>
          ))}

          {/* Message if clinic has no non-specialist doctors */}
          {nonSpecialistDoctors.length === 0 && (
            <div className="text-center text-white text-opacity-60 py-8 sm:py-12">
              <FaUserMd className="text-5xl sm:text-6xl mb-4 mx-auto opacity-50" />
              <p className="text-lg sm:text-xl mb-2">No doctors found</p>
              <p className="text-sm">
                Add non-specialist doctors to this clinic to manage working days
              </p>
            </div>
          )}
        </div>
      </div>
    </Card>
  );
};

export default WorkingDayCard;